import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, MotionConfig } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { createCheckoutSession, openCustomerPortal } from '../services/stripe';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';

const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

interface Subscription {
  stripe_customer_id: string | null;
  subscription_status: string | null;
}

/** Monochrome tick — charcoal, no green. Never repurposed as a color accent. */
const Tick = () => (
  <svg
    className="mt-0.5 h-4 w-4 flex-shrink-0 text-charcoal"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const FAQ_ITEMS = [
  {
    q: "What's a credit?",
    a: "Each credit lets you create one AI-powered debate or regenerate an existing one. 1 credit = 1 debate.",
  },
  {
    q: "What happens to unused subscription credits?",
    a: "Subscription credits refresh monthly and don't roll over. Credits from one-time packs never expire and stack on top of your subscription balance.",
  },
  {
    q: "Can I cancel my subscription?",
    a: "Yes, anytime. You keep access until the end of your billing period. Manage everything from the customer portal.",
  },
  {
    q: "Do I get free credits when I sign up?",
    a: "Yes — every new account starts with 3 free credits so you can try the product before committing.",
  },
  {
    q: "Can I buy multiple credit packs?",
    a: "Absolutely. Each pack adds 8 credits to your account. They stack with each other and with subscription credits.",
  },
];

const FaqItem: React.FC<{ q: string; a: string; defaultOpen?: boolean }> = ({
  q,
  a,
  defaultOpen = false,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left transition-colors hover:bg-black/[0.025]"
        aria-expanded={open}
      >
        <span className="text-[15px] font-semibold text-charcoal">{q}</span>
        <svg
          className={`h-4 w-4 flex-shrink-0 text-ink-muted transition-transform duration-300 ease-out-expo ${open ? 'rotate-45' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: EASE }}
            className="overflow-hidden"
          >
            <p className="text-pretty px-6 pb-5 text-[15px] leading-relaxed text-ink-muted">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Pricing: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<'monthly' | 'credit_pack' | 'portal' | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);

  const fetchSubscription = async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from('user_subscriptions')
        .select('stripe_customer_id, subscription_status')
        .eq('user_id', user.id)
        .single();
      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching subscription:', error);
        return;
      }
      setSubscription(data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  useEffect(() => {
    if (user) fetchSubscription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleSubscribe = async (priceType: 'monthly' | 'credit_pack') => {
    if (!user) {
      navigate('/signin');
      return;
    }
    try {
      setLoading(priceType);
      await createCheckoutSession(priceType, user.id, user.email!);
    } catch (error: any) {
      console.error('Error starting checkout:', error);
      alert(`Failed to start checkout: ${error.message}`);
      setLoading(null);
    }
  };

  const handleManageSubscription = async () => {
    if (!subscription?.stripe_customer_id) {
      alert('No subscription found');
      return;
    }
    try {
      setLoading('portal');
      await openCustomerPortal(subscription.stripe_customer_id);
    } catch (error: any) {
      alert(`Failed to open customer portal: ${error.message}`);
      setLoading(null);
    }
  };

  const hasActiveSubscription = subscription?.subscription_status === 'active';

  return (
    <MotionConfig reducedMotion="user">
      <div className="relative isolate min-h-screen w-full bg-white">
        {/* Blueprint grid — same canvas as landing and explore */}
        <div aria-hidden className="blueprint-grid pointer-events-none absolute inset-0 z-0" />

        <div className="relative z-10 mx-auto max-w-5xl px-6 py-16 sm:py-24">

          {/* ── Page header ── */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="mx-auto max-w-2xl text-center"
          >
            <span className="inline-block text-xs font-semibold uppercase tracking-eyebrow text-ink-muted">
              Simple pricing
            </span>
            <h1 className="mt-4 text-balance font-serif text-4xl tracking-tight text-charcoal sm:text-5xl">
              Argue more, pay less.
            </h1>
            <p className="mt-4 text-pretty text-lg leading-relaxed text-ink-muted">
              Every account starts with 3 free credits. Upgrade when you need more debates.
            </p>
          </motion.div>

          {/* ── Plan cards ── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE, delay: 0.1 }}
            className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-3"
          >
            {/* Free */}
            <div className="flex flex-col rounded-2xl border border-black/[0.07] bg-white/90 p-7 backdrop-blur-sm transition-all duration-300 ease-out-expo hover:-translate-y-1 hover:shadow-frame">
              <div>
                <span className="text-xs font-semibold uppercase tracking-eyebrow text-ink-muted">
                  Free
                </span>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="font-serif text-4xl text-charcoal">$0</span>
                  <span className="text-sm text-ink-muted">/ forever</span>
                </div>
                <p className="mt-2 text-sm text-ink-muted">
                  3 credits to start. No card needed.
                </p>
              </div>

              <ul className="mt-7 flex-grow space-y-3 border-t border-hairline pt-7">
                {[
                  '3 debate credits on signup',
                  'Access to all public debates',
                  'Vote on any debate',
                  'Create a free account',
                ].map(item => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-charcoal/80">
                    <Tick />
                    {item}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => navigate(user ? '/arena' : '/signup')}
                className="mt-8 w-full rounded-full border border-black/[0.10] bg-transparent py-3 text-sm font-semibold text-charcoal transition-colors hover:bg-charcoal hover:text-white"
              >
                {user ? 'Go to Arena' : 'Get started free'}
              </button>
            </div>

            {/* Credit Pack */}
            <div className="flex flex-col rounded-2xl border border-black/[0.07] bg-white/90 p-7 backdrop-blur-sm transition-all duration-300 ease-out-expo hover:-translate-y-1 hover:shadow-frame">
              <div>
                <span className="text-xs font-semibold uppercase tracking-eyebrow text-ink-muted">
                  Credit pack
                </span>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="font-serif text-4xl text-charcoal">$5</span>
                  <span className="text-sm text-ink-muted">/ one-time</span>
                </div>
                <p className="mt-2 text-sm text-ink-muted">
                  8 credits that never expire.
                </p>
              </div>

              <ul className="mt-7 flex-grow space-y-3 border-t border-hairline pt-7">
                {[
                  '8 debate credits',
                  'Credits never expire',
                  'No subscription required',
                  'Stack with other credits',
                ].map(item => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-charcoal/80">
                    <Tick />
                    {item}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleSubscribe('credit_pack')}
                disabled={loading !== null}
                className="mt-8 w-full rounded-full border border-black/[0.10] bg-transparent py-3 text-sm font-semibold text-charcoal transition-colors hover:bg-charcoal hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading === 'credit_pack' ? 'Redirecting…' : 'Buy 8 credits'}
              </button>
            </div>

            {/* Monthly — recommended, elevated */}
            <div className="relative flex flex-col rounded-2xl border border-charcoal/20 bg-white p-7 shadow-frame-lg transition-all duration-300 ease-out-expo hover:-translate-y-1 hover:shadow-lift">
              {/* Recommended badge */}
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="inline-block rounded-full bg-charcoal px-3 py-1 text-[10px] font-semibold uppercase tracking-eyebrow text-white">
                  Most popular
                </span>
              </div>

              <div>
                <span className="text-xs font-semibold uppercase tracking-eyebrow text-ink-muted">
                  Monthly
                </span>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="font-serif text-4xl text-charcoal">$10</span>
                  <span className="text-sm text-ink-muted">/ month</span>
                </div>
                <p className="mt-2 text-sm text-ink-muted">
                  20 credits, refreshed every month.
                </p>
              </div>

              <ul className="mt-7 flex-grow space-y-3 border-t border-hairline pt-7">
                {[
                  '20 debate credits monthly',
                  'Credits auto-refresh',
                  'Best value per credit',
                  'Cancel anytime',
                ].map(item => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-charcoal/80">
                    <Tick />
                    {item}
                  </li>
                ))}
              </ul>

              {hasActiveSubscription ? (
                <button
                  onClick={handleManageSubscription}
                  disabled={loading !== null}
                  className="mt-8 w-full rounded-full bg-charcoal py-3 text-sm font-semibold text-white transition-colors hover:bg-charcoal/85 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {loading === 'portal' ? 'Redirecting…' : 'Manage subscription'}
                </button>
              ) : (
                <button
                  onClick={() => handleSubscribe('monthly')}
                  disabled={loading !== null}
                  className="mt-8 w-full rounded-full bg-charcoal py-3 text-sm font-semibold text-white transition-colors hover:bg-charcoal/85 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {loading === 'monthly' ? 'Redirecting…' : 'Subscribe now'}
                </button>
              )}
            </div>
          </motion.div>

          {/* ── Credit context note ── */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.3 }}
            className="mt-6 text-center text-xs text-ink-muted"
          >
            1 credit = 1 debate. Purchased credits never expire and stack with your subscription balance.
          </motion.p>

          {/* ── FAQ ── */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: EASE, delay: 0.2 }}
            className="mx-auto mt-20 max-w-2xl"
          >
            <span className="eyebrow">FAQ</span>
            <h2 className="mt-3 text-balance font-serif text-2xl tracking-tight text-charcoal sm:text-3xl">
              Common questions
            </h2>

            <div className="mt-8 divide-y divide-black/[0.06] overflow-hidden rounded-2xl border border-black/[0.08] bg-white/85 backdrop-blur-sm">
              {FAQ_ITEMS.map(({ q, a }, i) => (
                <FaqItem key={q} q={q} a={a} defaultOpen={i === 0} />
              ))}
            </div>
          </motion.div>

        </div>
      </div>
    </MotionConfig>
  );
};

export default Pricing;
